import type {
  Bundle,
  Practitioner,
  Reference,
  Resource,
} from '@medplum/fhirtypes'
import { referenceDisplay } from '@seen/core/display'
import { QueuedEditsReviewTask } from '@seen/core/models/tasks/standard/queuedEditsReviewTask'
import { getBundleAiSummary } from '@seen/core/queued-edits/ai-summaries'
import { summarizeGroupIntents } from '@seen/core/queued-edits/group-summary'
import type {
  ResourceChangeSet,
  ResourceDecision,
} from '@seen/core/queued-edits/model'
import { getReferenceString } from '@seen/core/references'
import {
  Badge,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  cn,
  TypographyDetail,
  TypographySubtle,
} from '@seen/ui'
import {
  IconAlertTriangle,
  IconChecks,
  IconChevronDown,
  IconLink,
  IconLoader2,
  IconSparkles,
  IconX,
} from '@tabler/icons-react'
import {
  type Dispatch,
  Fragment,
  type JSX,
  type SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { AvatarCircles } from '#src/components/core/AvatarCircles.tsx'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import { ScrollArea } from '#src/components/core/scroll-area/ScrollArea.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { HoverTask } from '#src/components/HoverTask.tsx'
import { IconNewTask } from '#src/components/icons/IconNewTask.tsx'
import { PractitionerNameplate } from '#src/components/nameplates/PractitionerNameplate.tsx'
import { UserPlaceholderNameplate } from '#src/components/nameplates/UserPlaceholderNameplate.tsx'
import { PractitionerOption } from '#src/components/options/PractitionerOption.tsx'
import { UserPlaceholderOption } from '#src/components/options/UserPlaceholderOption.tsx'
import { QueuedEditsProposalSource } from '#src/components/queued-edits/QueuedEditsProposalSource.tsx'
import {
  DeciderAvatar,
  ResourceDiffCard,
} from '#src/components/queued-edits/ResourceDiffCard.tsx'
import { useBundleChangeSets } from '#src/components/queued-edits/useBundleChangeSets.ts'
import { useExpectedOwners } from '#src/components/queued-edits/useExpectedOwners.ts'
import { useQueuedEditDecisions } from '#src/components/queued-edits/useQueuedEditDecisions.ts'
import { useConfirmationStore } from '#src/hooks/useConfirmationStore.ts'
import { usePromoteBundleToTaskHandler } from '#src/hooks/usePromoteBundleToTaskHandler.ts'
import { useSearchOne } from '#src/hooks/useSearchOne.ts'

export type QueuedEditsReviewPhase = 'review' | 'processing' | 'done'

export interface QueuedEditsReviewProps {
  // Title of the change request, e.g. "Disenrollment cleanup — Robert Chen"
  // or an action label like "Disenroll Patient". Used only in the completion
  // message; the review header no longer renders it (the host surface owns the
  // title/description chrome).
  title: string
  // No longer rendered — kept for source compatibility with callers that pass
  // the host's description. The header omits it to avoid duplicating the task
  // page's own title/description sections.
  description?: string
  // The proposed change Bundle (batch or transaction). Mutually exclusive with
  // `changes`; one is required.
  bundle?: Bundle
  // Current server state for entries that mutate an existing resource, keyed by
  // `ResourceType/id`. Used to compute before → after diffs.
  current?: Record<string, Resource>
  // Pre-computed change sets — escape hatch / card-only stories. Prefer `bundle`.
  changes?: ResourceChangeSet[]
  // Shell styling. 'panel' = flat inbox panel; 'modal' = bordered + shadow + max width.
  variant?: 'panel' | 'modal'
  // Show a Cancel button in the footer (side-effect/modal flows). The merged
  // inbox + side-effect surface — `cancellable` is the toggle between them.
  cancellable?: boolean
  // Override the internal phase (story-only / externally driven apply).
  phase?: QueuedEditsReviewPhase
  // Require every resource reviewed before the confirm button enables.
  requireAllReviewed?: boolean
  // Reveal FHIR internals (raw values, FHIRPath labels, coding chips, patch
  // ops). Driven by an external flag; no in-UI toggle.
  showTechnical?: boolean
  confirmLabel?: string
  onConfirm?: (approvedIds: string[]) => void
  onCancel?: () => void
  // Seed decision state from previously-recorded decisions (rehydrate a
  // review interrupted before apply), keyed by ResourceChangeSet.id.
  initialDecisions?: Record<string, ResourceDecision>
  // Who recorded each seeded decision, keyed by ResourceChangeSet.id.
  initialDeciders?: Record<string, Reference<Practitioner>>
  // When each seeded decision was recorded (ISO), keyed by ResourceChangeSet.id.
  initialDecidedAt?: Record<string, string>
  // The practitioner attributed to decisions made this session. Drives the
  // decider avatar and the override-confirmation prompt.
  currentDecider?: Reference<Practitioner>
  // Persist a single decision change as the reviewer toggles (undefined =
  // cleared). Lets a host store decisions incrementally so they survive a
  // refresh before the final apply.
  onDecisionChange?: (
    id: string,
    decision: ResourceDecision | undefined
  ) => void
  // Persist a bulk approve/reject (resulting full decision map).
  onBulkDecisionChange?: (decisions: Record<string, ResourceDecision>) => void
  // When true (and driven by a `bundle`), render the inline "save this review
  // for later" affordance in the footer — promotes the proposal Bundle into a
  // QueuedEditsReviewTask, then links to it. Set only where the review has no
  // orchestrating Task yet (the inline ai-chat case). Only shown while reviewing.
  taskable?: boolean
  // Optionally lift the resource-type and owner filter state to the host. This
  // component is remounted on every bundle version bump (its parent keys it on
  // meta.versionId to re-seed decision state), which would reset internally-held
  // filter useState on each apply. A host above that key can own the filter state
  // so it survives the remount. Omit both to keep the filters self-managed
  // (stories / ai-chat, which never remount this way).
  activeTab?: string
  onActiveTabChange?: (tab: string) => void
  activeOwner?: string
  onActiveOwnerChange?: (owner: string | undefined) => void
  // Optionally lift the per-group expand/collapse state to the host, for the
  // same remount reason as the filters above — a decision write bumps the
  // bundle version and would otherwise snap every opened group shut. Grouped
  // sections collapse by default (the header's intent summary stands in for
  // the hidden cards). Omit both to keep it self-managed.
  expandedGroups?: string[]
  onExpandedGroupsChange?: (groups: string[]) => void
  ungroupedGroups?: string[]
  onUngroupedGroupsChange?: Dispatch<SetStateAction<string[]>>
}

// The resource-type filter sentinel meaning "no type filter". Exported so a host
// owning the filter state (see QueuedEditsReviewProps.activeTab) seeds the same
// default.
export const QUEUED_EDITS_ALL_TAB = '__all__'
const ALL_TAB = QUEUED_EDITS_ALL_TAB

const EMPTY_RESOURCE_CHANGE_SET: ResourceChangeSet[] = []

function expectedOwnerKey(change: ResourceChangeSet): string {
  if (change.expectedOwner?.kind === 'practitioner') {
    return change.expectedOwner.practitioner.reference ?? 'unowned'
  }
  if (change.expectedOwner?.kind === 'pcp') {
    const candidate = change.expectedOwner.candidate?.reference
    return candidate
      ? `pcp:${change.patientReference ?? 'unknown'}:${candidate}`
      : `pcp:${change.patientReference ?? 'unknown'}`
  }
  return 'unowned'
}

// Unified review surface for queued FHIR changes. Powers both the async inbox
// (variant="panel") and the post-action side-effect modal
// (variant="modal" cancellable). Per-resource decisions, bulk actions inline
// with the type-filter tabs, live progress in the footer, and a two-step
// confirm with processing/done phases.
export function QueuedEditsReview({
  title,
  bundle,
  current,
  changes: changesProp,
  variant = 'panel',
  cancellable = false,
  phase: phaseProp,
  requireAllReviewed = true,
  showTechnical = false,
  confirmLabel,
  onConfirm,
  onCancel,
  initialDecisions,
  initialDeciders,
  initialDecidedAt,
  currentDecider,
  onDecisionChange,
  onBulkDecisionChange,
  taskable = false,
  activeTab: activeTabProp,
  onActiveTabChange,
  activeOwner: activeOwnerProp,
  onActiveOwnerChange,
  expandedGroups: expandedGroupsProp,
  onExpandedGroupsChange,
  ungroupedGroups: ungroupedGroupsProp,
  onUngroupedGroupsChange,
}: QueuedEditsReviewProps): JSX.Element {
  // Filters are controlled when the host passes value + setter (so they survive
  // this component's remount-on-version-bump), otherwise self-managed.
  const [activeTabInternal, setActiveTabInternal] = useState<string>(ALL_TAB)
  const activeTab = activeTabProp ?? activeTabInternal
  const setActiveTab = onActiveTabChange ?? setActiveTabInternal
  const [internalPhase, setInternalPhase] =
    useState<QueuedEditsReviewPhase>('review')
  const phase = phaseProp ?? internalPhase
  // Expanded group labels. Absent = collapsed: a group decides as one unit, so
  // its members are detail the reviewer opts into.
  const [expandedGroupsInternal, setExpandedGroupsInternal] = useState<
    string[]
  >([])
  const expandedGroups = expandedGroupsProp ?? expandedGroupsInternal
  const setExpandedGroups = onExpandedGroupsChange ?? setExpandedGroupsInternal
  const [ungroupedGroupsInternal, setUngroupedGroupsInternal] = useState<
    string[]
  >([])
  const ungroupedGroups = ungroupedGroupsProp ?? ungroupedGroupsInternal
  const setUngroupedGroups =
    onUngroupedGroupsChange ?? setUngroupedGroupsInternal

  const {
    changes: bundleChanges,
    alreadyApplied,
    error: changesError,
  } = useBundleChangeSets(
    useMemo(() => (bundle ? { bundle, current } : undefined), [bundle, current])
  )
  const changes = bundle
    ? bundleChanges
    : (changesProp ?? EMPTY_RESOURCE_CHANGE_SET)
  // Entries whose proposed change was already made outside this review — not
  // decidable, but their removal must be visible (a silently shrinking review
  // reads as a bug, and approval semantics change when entries no-op away).
  const droppedCount = bundle ? alreadyApplied.length : 0

  const state = useQueuedEditDecisions(changes, {
    initialDecisions,
    initialDeciders,
    initialDecidedAt,
    currentDecider,
    onDecisionChange,
    onBulkChange: onBulkDecisionChange,
  })

  const resourceTypes = useMemo(
    () => [...new Set(changes.map((c) => c.resourceType))],
    [changes]
  )

  // Resolved expected owner per change (batched lookups) — drives both the
  // per-card owner chip and the owner filter tabs.
  const ownersById = useExpectedOwners(changes)
  const [activeOwnerInternal, setActiveOwnerInternal] = useState<
    string | undefined
  >()
  const activeOwner = activeOwnerProp ?? activeOwnerInternal
  const setActiveOwner = onActiveOwnerChange ?? setActiveOwnerInternal
  const ownerOptions = useMemo(() => {
    const byRef = new Map<string, { label: string; count: number }>()
    for (const change of changes) {
      const owner = ownersById[change.id]
      if (!owner?.reference) {
        continue
      }
      const existing = byRef.get(owner.reference)
      if (existing) {
        existing.count += 1
      } else {
        byRef.set(owner.reference, {
          label: referenceDisplay(owner) ?? 'Practitioner',
          count: 1,
        })
      }
    }
    return [...byRef.entries()].map(([reference, option]) => ({
      reference,
      ...option,
    }))
  }, [changes, ownersById])

  const visibleChanges = changes
    .filter((c) => activeTab === ALL_TAB || c.resourceType === activeTab)
    .filter((c) => !activeOwner || ownersById[c.id]?.reference === activeOwner)

  const currentMixedOwnerGroups = useMemo(() => {
    const ownersByGroup = new Map<string, Set<string>>()
    for (const change of changes) {
      if (!change.group) {
        continue
      }
      const owners = ownersByGroup.get(change.group) ?? new Set<string>()
      owners.add(expectedOwnerKey(change))
      ownersByGroup.set(change.group, owners)
    }
    return new Set(
      [...ownersByGroup.entries()].flatMap(([group, owners]) =>
        owners.size > 1 ? [group] : []
      )
    )
  }, [changes])
  useEffect(() => {
    setUngroupedGroups((current) => {
      const additions = [...currentMixedOwnerGroups].filter(
        (group) => !current.includes(group)
      )
      return additions.length > 0 ? [...current, ...additions] : current
    })
  }, [currentMixedOwnerGroups, setUngroupedGroups])
  const mixedOwnerGroups = useMemo(
    () => new Set([...ungroupedGroups, ...currentMixedOwnerGroups]),
    [ungroupedGroups, currentMixedOwnerGroups]
  )

  // Mixed-owner groups cannot safely expose one action that decides entries
  // belonging to another owner's filtered view, so the enhanced review treats
  // their members as individually reviewable. Other groups cluster by first
  // occurrence; the enhanced review promotes them ahead of ungrouped entries.
  const sections = useMemo(() => {
    interface Section {
      key: string
      group?: string
      changes: ResourceChangeSet[]
    }
    const grouped: Section[] = []
    const ungrouped: Section[] = []
    const byGroup = new Map<string, Section>()
    for (const change of visibleChanges) {
      if (!change.group || mixedOwnerGroups.has(change.group)) {
        ungrouped.push({ key: change.id, changes: [change] })
        continue
      }
      let section = byGroup.get(change.group)
      if (!section) {
        section = {
          key: `group-${change.group}`,
          group: change.group,
          changes: [],
        }
        byGroup.set(change.group, section)
        grouped.push(section)
      }
      section.changes.push(change)
    }
    return [...grouped, ...ungrouped]
  }, [visibleChanges, mixedOwnerGroups])

  // The bundle-level overview is only worth showing when there's more than one
  // change — for a single change the per-entry intent already says it all.
  const aiSummary =
    bundle && changes.length > 1 ? getBundleAiSummary(bundle) : undefined

  // An all-rejected review is still a completable outcome — submitting
  // records the decisions (and provenance) and completes the task, so the
  // only gate is that every change has been reviewed. A review whose every
  // entry was already applied outside it has nothing left to decide but must
  // remain completable (confirming records the outcome and closes the task).
  const confirmDisabled =
    requireAllReviewed &&
    !state.allReviewed &&
    !(changes.length === 0 && droppedCount > 0)

  const approvedIds = (): string[] =>
    changes.filter((c) => state.decisions[c.id] === 'approved').map((c) => c.id)

  // Distinct practitioners who have recorded any decision, for the read-only
  // overlay stack beside the progress bar.
  const uniqueDeciders = useMemo(() => {
    const seen = new Map<string, Reference<Practitioner>>()
    for (const ref of Object.values(state.deciders)) {
      const key = getReferenceString(ref)
      if (!seen.has(key)) {
        seen.set(key, ref)
      }
    }
    return [...seen.values()]
  }, [state.deciders])

  const openConfirmation = useConfirmationStore((s) => s.open)

  // Apply a decision, but first confirm if it would override a decision
  // recorded by a *different* reviewer (changing or clearing it). Own decisions
  // and undecided entries toggle freely.
  const decide = (
    change: ResourceChangeSet,
    decision: ResourceDecision
  ): void => {
    const existingDecider = state.deciders[change.id]
    const isOverride =
      existingDecider &&
      currentDecider &&
      getReferenceString(existingDecider) !== getReferenceString(currentDecider)
    if (isOverride) {
      const otherName = referenceDisplay(existingDecider) ?? 'another reviewer'
      const willClear = state.decisions[change.id] === decision
      openConfirmation({
        title: willClear ? 'Clear their decision?' : 'Override their decision?',
        description: (
          <>
            <span className="font-medium">{change.label}</span> was already{' '}
            {state.decisions[change.id] === 'approved'
              ? 'approved'
              : 'rejected'}{' '}
            by {otherName}.{' '}
            {willClear
              ? 'Clear their decision?'
              : `Replace it with your ${decision === 'approved' ? 'approval' : 'rejection'}?`}
          </>
        ),
        onConfirm: () => state.setDecision(change.id, decision),
      })
      return
    }
    state.setDecision(change.id, decision)
  }

  // Bulk approve/reject a set of changes (a group or everything), but confirm
  // first if it would overwrite any decisions recorded by other reviewers.
  // With `toggle`, re-clicking the decision the whole set already carries
  // clears it instead (mirroring the single-card toggle-off) — the group
  // header buttons use this.
  const decideMany = (
    targets: ResourceChangeSet[],
    decision: ResourceDecision,
    { toggle = false }: { toggle?: boolean } = {}
  ): void => {
    const ids = targets.map((c) => c.id)
    const willClear =
      toggle && ids.every((id) => state.decisions[id] === decision)
    const apply = (): void =>
      willClear ? state.clearMany(ids) : state.setMany(ids, decision)
    const overriddenBy = new Set<string>()
    for (const change of targets) {
      const existing = state.deciders[change.id]
      if (
        existing &&
        currentDecider &&
        getReferenceString(existing) !== getReferenceString(currentDecider)
      ) {
        overriddenBy.add(referenceDisplay(existing) ?? 'another reviewer')
      }
    }
    if (overriddenBy.size === 0) {
      apply()
      return
    }
    const names = [...overriddenBy]
    const who =
      names.length === 1
        ? names[0]
        : `${names.slice(0, -1).join(', ')} and ${names.at(-1)}`
    openConfirmation({
      title: willClear
        ? 'Clear their decisions?'
        : `Override decisions by ${names.length === 1 ? 'another reviewer' : 'other reviewers'}?`,
      description: willClear ? (
        <>
          This will clear decisions already made by{' '}
          <span className="font-medium">{who}</span>. Continue?
        </>
      ) : (
        <>
          {decision === 'approved' ? 'Approving' : 'Rejecting'} these will
          replace decisions already made by{' '}
          <span className="font-medium">{who}</span>. Continue?
        </>
      ),
      onConfirm: apply,
    })
  }

  const shell = (children: JSX.Element): JSX.Element => (
    <div
      className={cn(
        // w-full so the card fills its container at every collapse state —
        // otherwise it shrinks to content while collapsed and jumps wider when
        // a diff table expands, shifting surrounding chat content.
        'flex w-full min-w-0 flex-col rounded-lg border border-border bg-card',
        variant === 'modal' && 'max-w-2xl shadow-lg'
      )}
    >
      {children}
    </div>
  )

  if (phase === 'processing') {
    return shell(
      <div className="flex flex-col items-center justify-center gap-3 py-16">
        <IconLoader2 className="size-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">
          {state.approvedCount > 0
            ? `Applying ${state.approvedCount} changes…`
            : 'Saving review…'}
        </p>
      </div>
    )
  }

  if (phase === 'done') {
    return shell(
      <div className="flex flex-col items-center justify-center gap-3 py-16">
        <div className="flex size-12 items-center justify-center rounded-full bg-pine-100">
          <IconChecks className="size-6 text-pine-600" />
        </div>
        <p className="text-sm font-medium">
          {title} complete —{' '}
          {state.approvedCount > 0
            ? `${state.approvedCount} changes applied`
            : 'all changes rejected'}
        </p>
        {cancellable && (
          <Button variant="outline" size="sm" onClick={onCancel}>
            Close
          </Button>
        )}
      </div>
    )
  }

  return shell(
    <>
      {/* Header. Title/description are intentionally omitted — the task page
          already renders those; repeating them here is redundant. A single
          change needs neither the type-filter tabs nor the bulk actions (the
          one card's own approve/reject covers it), and the bundle overview is
          already suppressed — so the whole header is dropped in that case. */}
      {(aiSummary || changes.length > 1 || droppedCount > 0) && (
        <div className="flex flex-col gap-3 border-b border-border px-5 py-4">
          {aiSummary && (
            <p className="w-full max-w-none flex-1 text-xs text-muted-foreground">
              <IconSparkles className="mr-1 inline size-3 shrink-0 text-primary" />
              {aiSummary}
            </p>
          )}

          {/* Drift visibility: entries whose change was already made outside
              this review are dropped from the decidable list, but the shrinkage
              must be legible — silently vanished rows read as a bug and change
              what approval means. */}
          {droppedCount > 0 && (
            <p className="w-full text-xs text-muted-foreground">
              {droppedCount === 1
                ? '1 proposed change was already made outside this review and was removed.'
                : `${droppedCount} proposed changes were already made outside this review and were removed.`}
            </p>
          )}

          {/* Condensed type-filter tabs (inbox-style ghost buttons). No
              approve-all/reject-all — blanket actions invite automation bias;
              every change (or group) must be decided deliberately. The tab
              strip can outgrow the card width — scroll it horizontally instead
              of spilling past the border, keeping the styled ScrollArea track
              rather than a native scrollbar. The owner facet lives OUTSIDE the
              scroll strip, pinned to the right — a compact practitioner
              combobox (an independent filter dimension, AND-combined with the
              type tabs). */}
          {changes.length > 1 && (
            <div className="flex items-center gap-2">
              <ScrollArea
                orientation="horizontal"
                scrollRestorationId={`queued-edits-review-filter-tabs-${bundle?.id}`}
                className="h-auto min-w-0 flex-1"
              >
                <div className="flex w-max items-center gap-1">
                  <FilterTab
                    label="All"
                    count={changes.length}
                    selected={activeTab === ALL_TAB}
                    onSelect={() => setActiveTab(ALL_TAB)}
                  />
                  {resourceTypes.map((rt) => (
                    <FilterTab
                      key={rt}
                      label={rt}
                      count={
                        changes.filter((c) => c.resourceType === rt).length
                      }
                      selected={activeTab === rt}
                      onSelect={() => setActiveTab(rt)}
                    />
                  ))}
                </div>
              </ScrollArea>
              {(ownerOptions.length > 0 || activeOwner) && (
                <OwnerFilterCombobox
                  owners={ownerOptions}
                  value={activeOwner ?? null}
                  onChange={(value) => setActiveOwner(value ?? undefined)}
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* Cards — flat, edge-to-edge; each card draws its own bottom border.
          Caps at a max height and scrolls past it; shrinks to content below the
          cap. `h-auto!` + `!max-h` override the ScrollArea root's `size-full`;
          the contentClassName caps the Radix viewport too so its own `size-full`
          doesn't outgrow the cap and defeat the scroll. */}
      <ScrollArea
        scrollRestorationId={`queued-edits-review-${bundle?.id}`}
        className="h-auto! max-h-[480px]!"
        contentClassName="max-h-[480px]!"
      >
        {changesError && (
          <p className="px-5 py-3 text-sm text-destructive">
            Could not compute the proposed changes: {changesError.message}
          </p>
        )}
        {sections.map((section) => {
          // Grouped entries decide as ONE unit: the header carries the only
          // approve/reject controls and the member cards render their shared
          // decision read-only (no per-card buttons). Group actions target
          // every group member, not just the ones visible under the current
          // type-filter tab. The group's left accent bar runs continuously
          // through the sticky header and every member card.
          //
          // A grouped section is wrapped in a div so its `sticky top-0` header
          // releases at the section's bottom instead of staying pinned past its
          // own cards. That wrapper makes the section's final card `:last-child`
          // (its `last:border-b-0` drops the border), so the wrapper carries the
          // `border-b` that separates it from the next section. Ungrouped
          // sections stay bare fragments (no sticky header, nothing to release),
          // so each of their cards keeps drawing its own bottom-border separator.
          const groupTargets = section.group
            ? changes.filter((c) => c.group === section.group)
            : section.changes
          const groupDecision = (['approved', 'rejected'] as const).find(
            (decision) =>
              groupTargets.every((c) => state.decisions[c.id] === decision)
          )
          // One accent for the whole unit: the header and every member card
          // draw the same bar, so the group reads as a single bracketed
          // decision. Undecided groups get a visible neutral (an ungrouped
          // card's undecided bar is transparent) — the bar is what marks the
          // members as one unit, so it has to show before any decision.
          const groupAccent =
            groupDecision === 'approved'
              ? 'border-l-pine-500'
              : groupDecision === 'rejected'
                ? 'border-l-red-400'
                : 'border-l-muted-foreground/40'
          const cards = section.changes.map((change) => (
            <ResourceDiffCard
              key={change.id}
              change={change}
              showTechnical={showTechnical}
              decision={state.decisions[change.id]}
              decidedBy={state.deciders[change.id]}
              decidedAt={state.decidedAt[change.id]}
              expectedOwner={ownersById[change.id]}
              {...(section.group
                ? { className: cn('border-l-4', groupAccent) }
                : {
                    onApprove: () => decide(change, 'approved'),
                    onReject: () => decide(change, 'rejected'),
                  })}
            />
          ))
          if (!section.group) {
            return <Fragment key={section.key}>{cards}</Fragment>
          }
          const groupDecider = groupTargets
            .map((c) => state.deciders[c.id])
            .find(Boolean)
          const groupDecidedAt = groupTargets
            .map((c) => state.decidedAt[c.id])
            .find(Boolean)
          const groupActions = (
            <div className="ml-auto flex shrink-0 items-center gap-1.5">
              {groupDecision && groupDecider && (
                <DeciderAvatar
                  decidedBy={groupDecider}
                  decidedAt={groupDecidedAt}
                />
              )}
              <Button
                variant={
                  groupDecision === 'rejected' ? 'destructive' : 'outline'
                }
                size="xs"
                onClick={() =>
                  decideMany(groupTargets, 'rejected', { toggle: true })
                }
              >
                <IconX className="size-3.5" />
                Reject
              </Button>
              <Button
                variant={groupDecision === 'approved' ? 'default' : 'outline'}
                size="xs"
                onClick={() =>
                  decideMany(groupTargets, 'approved', { toggle: true })
                }
                className={cn(
                  groupDecision === 'approved' &&
                    'bg-pine-600 text-white hover:bg-pine-700'
                )}
              >
                <IconChecks className="size-3.5" />
                Approve
              </Button>
            </div>
          )
          const groupLabel = section.group
          const groupExpanded = expandedGroups.includes(groupLabel)
          // Members are hidden by default, so the header carries the "what
          // does approving this do" answer itself.
          const groupSummary = summarizeGroupIntents(groupTargets)
          // Drift must survive the collapse — a stale member hidden inside a
          // closed group is exactly the case the per-card badge exists for.
          const driftedCount = groupTargets.filter(
            (c) => c.changedSinceProposed
          ).length
          return (
            <Collapsible
              key={section.key}
              asChild
              open={groupExpanded}
              onOpenChange={(open) =>
                setExpandedGroups(
                  open
                    ? [...new Set([...expandedGroups, groupLabel])]
                    : expandedGroups.filter((group) => group !== groupLabel)
                )
              }
            >
              <div className="border-b border-border">
                <div
                  data-testid="queued-edits-group-header"
                  className={cn(
                    'sticky top-0 z-10 flex items-center gap-2 border-border border-b border-l-4 bg-muted px-4 py-1.5 backdrop-blur-sm transition-colors supports-backdrop-filter:bg-muted/80',
                    groupAccent
                  )}
                >
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      aria-label={
                        groupExpanded ? 'Collapse group' : 'Expand group'
                      }
                      className="shrink-0 rounded p-0.5 text-muted-foreground hover:bg-background hover:text-foreground"
                    >
                      <IconChevronDown
                        className={cn(
                          'size-4 transition-transform',
                          groupExpanded && 'rotate-180'
                        )}
                      />
                    </button>
                  </CollapsibleTrigger>
                  <IconLink className="size-3.5 shrink-0 text-muted-foreground" />
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <TypographySubtle className="shrink-0">
                      {groupLabel}
                    </TypographySubtle>
                    <TypographyDetail className="shrink-0 text-muted-foreground">
                      {groupTargets.length}
                    </TypographyDetail>
                    {groupSummary && (
                      <>
                        <span
                          aria-hidden
                          className="shrink-0 text-xs text-muted-foreground"
                        >
                          ·
                        </span>
                        <span
                          title={groupSummary}
                          className="min-w-0 truncate text-xs text-muted-foreground"
                        >
                          {groupSummary}
                        </span>
                      </>
                    )}
                    {!groupExpanded && driftedCount > 0 && (
                      <Badge
                        className="shrink-0 whitespace-nowrap"
                        color="yellow"
                        variant="outline"
                      >
                        <IconAlertTriangle className="size-3" />
                        {driftedCount} changed since proposed
                      </Badge>
                    )}
                  </div>
                  {groupActions}
                </div>
                <CollapsibleContent>{cards}</CollapsibleContent>
              </div>
            </Collapsible>
          )
        })}
      </ScrollArea>

      {/* Footer: provenance and the inline save-for-later action at the
          bottom-left, the confirm action at the right. */}
      <div className="flex items-center gap-2 border-t border-border px-3 py-2">
        <div className="flex min-w-0 flex-1 items-center gap-1">
          {bundle?.id && (
            <QueuedEditsProposalSource
              bundleReference={`${bundle.resourceType}/${bundle.id}`}
              showTechnical={showTechnical}
              className="min-w-0 truncate"
            />
          )}
          {taskable && bundle?.id && (
            <div className="shrink-0">
              <QueuedEditsTaskAction
                bundleReference={`${bundle.resourceType}/${bundle.id}`}
              />
            </div>
          )}
        </div>
        {uniqueDeciders.length > 0 && (
          <DeciderStack deciders={uniqueDeciders} />
        )}
        {cancellable && (
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          size="sm"
          disabled={confirmDisabled}
          onClick={() => {
            setInternalPhase('processing')
            onConfirm?.(approvedIds())
          }}
        >
          {confirmDisabled ? 'Review all to apply' : (confirmLabel ?? 'Apply')}
        </Button>
      </div>
    </>
  )
}

// Inline "save this review for later" affordance: an icon button that promotes
// the proposal Bundle into a QueuedEditsReviewTask (opening the task composer to
// confirm + assign an owner), switching to a link to that task once filed. The
// link is recovered on reload via the searchable `Task.focus` (byBundle), so it
// survives past the in-session promote callback.
function QueuedEditsTaskAction({
  bundleReference,
}: {
  bundleReference: string
}): JSX.Element {
  const promote = usePromoteBundleToTaskHandler()
  const [promotedTaskReference, setPromotedTaskReference] = useState<string>()

  const existingTask = useSearchOne(
    QueuedEditsReviewTask,
    QueuedEditsReviewTask.byBundle({ reference: bundleReference })
  )
  const taskReference =
    promotedTaskReference ?? existingTask.data?.reference.reference

  if (taskReference) {
    // HoverTask resolves the task + its participant and links to the
    // patient-scoped task route (`<participant.relativeUrl>/tasks/<id>`).
    return (
      <span className="inline-flex items-center text-xs">
        <HoverTask task={{ reference: taskReference }} />
      </span>
    )
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Save for later"
          onClick={() => promote(bundleReference, setPromotedTaskReference)}
        >
          <IconNewTask className="size-3.5" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>Save for later</TooltipContent>
    </Tooltip>
  )
}

// Condensed resource-type filter tab, styled like the inbox category bar: a
// ghost button with the count as a muted trailing detail.
function FilterTab({
  label,
  count,
  selected,
  onSelect,
}: {
  label: string
  count: number
  selected: boolean
  onSelect: () => void
}): JSX.Element {
  return (
    <Button variant="ghost" size="xs" selected={selected} onClick={onSelect}>
      {label}
      {count > 0 && (
        <TypographyDetail className="ml-0.5 text-muted-foreground">
          {count}
        </TypographyDetail>
      )}
    </Button>
  )
}

// Compact owner-facet filter: a combobox over the review's resolved expected
// owners (not the whole practitioner directory — only people who actually own
// something here). The trigger renders the selected practitioner's nameplate
// (avatar + resolved name), so a reference without a display string still
// shows the person. Selecting "All owners" clears the facet.
function OwnerFilterCombobox({
  owners,
  value,
  onChange,
}: {
  owners: { reference: string; label: string; count: number }[]
  value: string | null
  onChange: (value: string | null) => void
}): JSX.Element {
  const options = useMemo(
    () => [
      {
        value: null,
        label: (
          <UserPlaceholderOption isSelected={!value}>
            All owners
          </UserPlaceholderOption>
        ),
        keywords: ['All owners'],
      },
      ...owners.map((owner) => ({
        value: owner.reference,
        label: (
          <PractitionerOption
            practitioner={{ reference: owner.reference }}
            isSelected={owner.reference === value}
          />
        ),
        keywords: [owner.label, owner.reference],
      })),
    ],
    [owners, value]
  )
  return (
    <Combobox options={options} onSelect={onChange}>
      <Button
        variant="outline"
        size="xs"
        className="shrink-0 gap-1 shadow-sm"
        aria-label="Filter by owner"
      >
        {value ? (
          <PractitionerNameplate practitioner={value} size={3} />
        ) : (
          <UserPlaceholderNameplate size="xs">Owner</UserPlaceholderNameplate>
        )}
      </Button>
    </Combobox>
  )
}

const MAX_VISIBLE_DECIDERS = 5

// Read-only overlapping avatar stack of the reviewers who have decided so far —
// purely informational, mirroring the subscribe-button overlay.
function DeciderStack({
  deciders,
}: {
  deciders: Reference<Practitioner>[]
}): JSX.Element {
  const visible = deciders.slice(0, MAX_VISIBLE_DECIDERS)
  const overflow = deciders.length - visible.length
  return (
    <div className="shrink-0" title="Reviewers who have decided">
      <AvatarCircles
        avatars={visible}
        numInLastCircle={overflow > 0 ? overflow : undefined}
      />
    </div>
  )
}
